import { sanitizeHtml } from "@/lib/sanitize";
import { cleanHtmlToText } from "@/lib/clean-html-text";
import { createFileRoute, Link, notFound } from "@tanstack/react-router";
import { useServerFn } from "@tanstack/react-start";
import { useMutation, useQuery } from "@tanstack/react-query";
import { useEffect, useState } from "react";
import { Heart, Mail, Minus, Plus, Store, Instagram, Youtube, Linkedin, Facebook, Phone, MapPin, Share2, Copy } from "lucide-react";
import { ProductCard } from "@/components/ProductCard";
import { Reviews } from "@/components/Reviews";
import { listReviews } from "@/lib/reviews.functions";

import { WishlistButton } from "@/components/WishlistButton";
import { getProduct } from "@/lib/catalog.functions";
import { useCart } from "@/lib/cart";
import { useAuth } from "@/lib/auth";
import { useNavigate } from "@tanstack/react-router";
import { createInquiry } from "@/lib/inquiries.functions";
import { toast } from "sonner";
import { getMyWishlistIds, toggleWishlist } from "@/lib/wishlist.functions";
import { trackVendorVisit } from "@/lib/vendor-visits.functions";
import { useQueryClient } from "@tanstack/react-query";
import { formatPhone } from "@/lib/formatPhone";
import { buildShippingDetails, MERCHANT_RETURN_POLICY, PRODUCT_DESCRIPTION_FALLBACK } from "@/lib/product-schema";
import { productImageAlt } from "@/lib/image-alt";

export const Route = createFileRoute("/izdelki/$slug")({
  loader: async ({ params }) => {
    const res = await getProduct({ data: { slug: params.slug } });
    const p = res?.product as { id?: string; title?: string; short_description?: string | null; description?: string | null; featured_image_url?: string | null; price?: number | string | null; sale_price?: number | string | null } | null;
    const v = (res as unknown as { product?: { vendor?: { store_name?: string; shipping_fee?: number | string | null; free_shipping_over?: number | string | null } | null } } | null)?.product?.vendor ?? null;
    if (!p) throw notFound();
    const raw = cleanHtmlToText(p.short_description || p.description || "", 1000);
    const toNum = (x: number | string | null | undefined) =>
      typeof x === "number" ? x : (x ? parseFloat(String(x).replace(/[^0-9.,]/g, "").replace(",", ".")) : null);
    const regularNum = toNum(p.price);
    const saleNum = toNum(p.sale_price);
    const priceNum = saleNum != null && regularNum != null && saleNum < regularNum ? saleNum : regularNum;
    // Ocene kupcev → aggregateRating v strukturiranih podatkih (zvezdice v Googlu).
    let rating: { value: number; count: number } | null = null;
    if (p.id) {
      try {
        const r = await listReviews({ data: { productId: p.id } });
        if (r?.count > 0) rating = { value: Math.round(r.avg * 10) / 10, count: r.count };
      } catch {
        rating = null;
      }
    }
    return {
      meta: {
        title: p.title ?? params.slug,
        description: raw.slice(0, 160) || `${p.title ?? ""} — nov izdelek ponudnika${v?.store_name ? ` ${v.store_name}` : ""}.`,
        image: p.featured_image_url ?? null,
        vendor: v?.store_name ?? null,
        price: priceNum,
        shippingFee: toNum(v?.shipping_fee),
        freeShippingOver: toNum(v?.free_shipping_over),
        rating,
      },
    };

  },
  head: ({ params, loaderData }) => {
    const m = loaderData?.meta;
    const url = `https://kupish.si/izdelki/${params.slug}`;
    // Build a title ≤60 chars: try product+vendor+suffix, then product+suffix, then product alone, then truncate.
    const SUFFIX = " | kupish.si";
    const buildTitle = () => {
      if (!m) return "Izdelek — kupish.si";
      const withVendor = `${m.title}${m.vendor ? ` — ${m.vendor}` : ""}${SUFFIX}`;
      if (withVendor.length <= 60) return withVendor;
      const withoutVendor = `${m.title}${SUFFIX}`;
      if (withoutVendor.length <= 60) return withoutVendor;
      if (m.title.length <= 60) return m.title;
      return m.title.slice(0, 57).trimEnd() + "…";
    };
    const title = buildTitle();
    const FALLBACK_DESC = "Nov izdelek preverjenega ponudnika na kupish.si — preverite opis, ceno in pogoje dostave.";
    const baseDesc = m?.description ?? "";
    const desc = baseDesc.length >= 50 ? baseDesc : (baseDesc ? `${baseDesc} — ${FALLBACK_DESC}`.slice(0, 160) : FALLBACK_DESC);
    const schemas: object[] = [
      {
        "@context": "https://schema.org",
        "@type": "BreadcrumbList",
        itemListElement: [
          { "@type": "ListItem", position: 1, name: "Domov", item: "https://kupish.si/" },
          { "@type": "ListItem", position: 2, name: "Izdelki", item: "https://kupish.si/izdelki" },
          { "@type": "ListItem", position: 3, name: m?.title ?? params.slug, item: url },
        ],
      },
    ];
    if (m) {
      const productSchema: Record<string, unknown> = {
        "@context": "https://schema.org",
        "@type": "Product",
        name: m.title,
        description: desc,
        url,
        ...(m.image ? { image: m.image } : {}),
        ...(m.vendor ? { brand: { "@type": "Brand", name: m.vendor } } : {}),
      };
      if (typeof m.price === "number" && !isNaN(m.price)) {
        productSchema.offers = {
          "@type": "Offer",
          price: m.price,
          priceCurrency: "EUR",
          url,
          availability: "https://schema.org/InStock",
          shippingDetails: buildShippingDetails(m.shippingFee, m.freeShippingOver),
          hasMerchantReturnPolicy: MERCHANT_RETURN_POLICY,
        };
      }
      if (m.rating) {
        productSchema.aggregateRating = {
          "@type": "AggregateRating",
          ratingValue: m.rating.value,
          reviewCount: m.rating.count,
          bestRating: 5,
          worstRating: 1,
        };
      }

      schemas.unshift(productSchema);
    }
    return {
      meta: [
        { title },
        { name: "description", content: desc },
        { name: "robots", content: m ? "index, follow, max-image-preview:large, max-snippet:-1" : "noindex, follow" },
        { property: "og:title", content: title },
        { property: "og:description", content: desc },
        { property: "og:url", content: url },
        { property: "og:type", content: "product" },
        { property: "og:locale", content: "sl_SI" },
        { property: "og:site_name", content: "kupish.si" },
        { name: "twitter:card", content: m?.image ? "summary_large_image" : "summary" },
        { name: "twitter:title", content: title },
        { name: "twitter:description", content: desc },
        ...(m?.image ? [
          { property: "og:image", content: m.image.startsWith("http") ? m.image : `https://kupish.si${m.image}` },
          { property: "og:image:secure_url", content: m.image.startsWith("http") ? m.image : `https://kupish.si${m.image}` },
          { property: "og:image:alt", content: title },
          { name: "twitter:image", content: m.image.startsWith("http") ? m.image : `https://kupish.si${m.image}` },
          { name: "twitter:image:alt", content: title },
        ] : []),
        ...(typeof m?.price === "number" && !isNaN(m.price) ? [
          { property: "product:price:amount", content: String(m.price) },
          { property: "product:price:currency", content: "EUR" },
          { property: "product:availability", content: "in stock" },
          { name: "twitter:label1", content: "Cena" },
          { name: "twitter:data1", content: `${m.price.toFixed(2).replace(".", ",")} €` },
        ] : []),
        ...(m?.vendor ? [
          { property: "product:brand", content: m.vendor },
          { name: "twitter:label2", content: "Prodajalec" },
          { name: "twitter:data2", content: m.vendor },
        ] : []),

      ],
      links: [
        { rel: "canonical", href: url },
        ...(m?.image ? [{ rel: "preload", as: "image", href: m.image, fetchpriority: "high" }] : []),
      ],
      scripts: schemas.map((s) => ({
        type: "application/ld+json",
        children: JSON.stringify(s),
      })),
    };
  },
  notFoundComponent: () => (
    <section className="mx-auto max-w-3xl px-6 py-24 text-center">
      <h1 className="font-serif text-4xl">Izdelek ni najden</h1>
      <p className="mt-4 text-muted-foreground">Izdelek ne obstaja ali ni več na voljo.</p>
      <Link to="/izdelki" className="mt-6 inline-block text-accent">Poglejte druge izdelke</Link>
    </section>
  ),
  component: Page,
});

function ensureUrl(url: string | null, host?: string): string | null {
  if (!url) return null;
  const trimmed = url.trim().replace(/^@/, "");
  if (!trimmed) return null;
  if (/^https?:\/\//i.test(trimmed)) return trimmed;
  if (host && !trimmed.includes("/") && !trimmed.includes(".")) {
    return `https://${host}/${trimmed}`;
  }
  return `https://${trimmed}`;
}

function Page() {
  const { slug } = Route.useParams();
  const fetchProduct = useServerFn(getProduct);
  const { data, isLoading } = useQuery({
    queryKey: ["product", slug],
    queryFn: () => fetchProduct({ data: { slug } }),
  });
  const { add } = useCart();
  const { user } = useAuth();
  const navigate = useNavigate();
  const qc = useQueryClient();
  const [activeImage, setActiveImage] = useState(0);
  const [qty, setQty] = useState(1);
  const [shareOpen, setShareOpen] = useState(false);
  const [detailTab, setDetailTab] = useState<"opis" | "tehnicne">("opis");
  const shareUrl = typeof window !== "undefined" ? window.location.href : `https://kupish.si/izdelki/${slug}`;
  const [inquiryOpen, setInquiryOpen] = useState(false);
  const [inquiryEmail, setInquiryEmail] = useState("");
  const [inquiryMessage, setInquiryMessage] = useState("");
  const [inquiryStatus, setInquiryStatus] = useState<"idle" | "success" | "error">("idle");
  const [inquiryError, setInquiryError] = useState<string | null>(null);

  const sendInquiry = useServerFn(createInquiry);
  const fetchWishlistIds = useServerFn(getMyWishlistIds);
  const toggleWish = useServerFn(toggleWishlist);
  const { data: wishData } = useQuery({
    queryKey: ["wishlist-ids"],
    queryFn: () => fetchWishlistIds(),
    enabled: !!user,
  });
  const wishMutation = useMutation({
    mutationFn: (pid: string) => toggleWish({ data: { product_id: pid } }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["wishlist-ids"] });
      qc.invalidateQueries({ queryKey: ["my-wishlist"] });
    },
  });
  const inquiryMutation = useMutation({
    mutationFn: (vars: { product_id: string; email: string; message: string }) => sendInquiry({ data: vars }),
    onSuccess: () => {
      setInquiryStatus("success");
      setInquiryMessage("");
    },
    onError: (err: Error) => {
      setInquiryStatus("error");
      setInquiryError(err.message);
    },
  });

  const product = data?.product ?? null;
  const vendor = (product as unknown as {
    vendor: {
      id: string;
      store_name: string;
      slug: string | null;
      city: string | null;
      shipping_fee?: number | string | null;
      free_shipping_over?: number | string | null;
      description?: string | null;
      instagram?: string | null;
      youtube?: string | null;
      linkedin?: string | null;
      facebook?: string | null;
      tiktok?: string | null;
      email?: string | null;
      phone?: string | null;
    } | null;
  } | null)?.vendor ?? null;
  const trackVisit = useServerFn(trackVendorVisit);
  useEffect(() => {
    if (!vendor?.id || !product?.id) return;
    trackVisit({ data: { vendorId: vendor.id, kind: "product", productId: product.id, path: `/izdelki/${product.slug ?? product.id}` } }).catch(() => {});
  }, [vendor?.id, product?.id, product?.slug, trackVisit]);

  if (isLoading) return <div className="mx-auto max-w-5xl px-6 py-24 text-muted-foreground">Nalagam…</div>;
  if (!product) {
    return (
      <section className="mx-auto max-w-3xl px-6 py-24 text-center">
        <h1 className="font-serif text-4xl">Izdelek ni najden</h1>
        <Link to="/izdelki" className="mt-6 inline-block text-accent">Poglejte druge izdelke</Link>
      </section>
    );
  }
  const regularPrice = product.price != null ? Number(product.price) : null;
  const salePrice = (product as { sale_price?: number | string | null }).sale_price != null
    ? Number((product as { sale_price?: number | string | null }).sale_price)
    : null;
  const hasSale = salePrice != null && regularPrice != null && salePrice < regularPrice;
  const price = hasSale ? salePrice : regularPrice;
  const outOfStock = product.in_stock === false || (product.stock_quantity != null && product.stock_quantity <= 0);

  const images = [product.featured_image_url, ...(product.gallery_urls ?? [])].filter((u): u is string => !!u);
  const currentImage = images[activeImage] ?? product.featured_image_url;
  const shippingFee = vendor?.shipping_fee != null ? Number(vendor.shipping_fee) : null;
  const freeOver = vendor?.free_shipping_over != null ? Number(vendor.free_shipping_over) : null;

  const ld = {
    "@context": "https://schema.org",
    "@type": "Product",
    name: product.title,
    image: images.length > 0 ? images : undefined,
    description:
      cleanHtmlToText(product.short_description || product.description || "", 500) ||
      `${product.title} — ${PRODUCT_DESCRIPTION_FALLBACK}`,
    brand: vendor?.store_name ? { "@type": "Brand", name: vendor.store_name } : undefined,
    offers: price != null ? {
      "@type": "Offer",
      price: price.toFixed(2),
      priceCurrency: "EUR",
      availability: outOfStock ? "https://schema.org/OutOfStock" : "https://schema.org/InStock",
      shippingDetails: buildShippingDetails(shippingFee, freeOver),
      hasMerchantReturnPolicy: MERCHANT_RETURN_POLICY,
    } : undefined,
  };

  return (
    <>
      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{
          __html: JSON.stringify(ld)
            .replace(/</g, "\\u003c")
            .replace(/>/g, "\\u003e")
            .replace(/&/g, "\\u0026")
            .replace(/\u2028/g, "\\u2028")
            .replace(/\u2029/g, "\\u2029"),
        }}
      />
      <section className="mx-auto max-w-7xl px-6 py-12 md:py-16">
        <div className="grid md:grid-cols-2 gap-12">
          <div>
            <div className="aspect-[4/5] bg-muted overflow-hidden relative">
              {currentImage && (
                <img src={currentImage} alt={productImageAlt(product.title, vendor?.store_name, activeImage, images.length)} width={800} height={1000} loading="eager" fetchPriority="high" decoding="async" className="h-full w-full object-cover" />
              )}
              <WishlistButton productId={product.id} className="absolute top-4 right-4" />
            </div>
            {images.length > 1 && (
              <div className="mt-4 grid grid-cols-5 gap-2">
                {images.map((url, i) => (
                  <button key={url + i} type="button" onClick={() => setActiveImage(i)}
                    className={`aspect-square overflow-hidden bg-muted border ${i === activeImage ? "border-foreground" : "border-transparent hover:border-border"}`}>
                    <img src={url} alt={productImageAlt(product.title, vendor?.store_name, i, images.length)} width={160} height={160} loading="lazy" decoding="async" className="h-full w-full object-cover" />
                  </button>
                ))}
              </div>
            )}
          </div>
          <div>
            {vendor && (
              <div className="flex items-center gap-2">
                <span className="text-sm text-muted-foreground">Prodajalec:</span>
                <Link
                  to="/prodajalci/$slug"
                  params={{ slug: vendor.slug! }}
                  className="flex items-center gap-2 group"
                  title="Poglej več izdelkov ponudnika"
                >
                  <span className="font-serif text-2xl md:text-3xl text-brand group-hover:text-accent transition-colors">
                    {vendor.store_name}
                  </span>
                  <Store className="h-5 w-5 text-muted-foreground group-hover:text-accent transition-colors" />
                </Link>
              </div>
            )}
            <h1 className="font-serif tracking-tight text-4xl md:text-5xl mt-4 mb-6 uppercase">{product.title}</h1>
            {price != null && (
              <div className="mt-2 flex flex-wrap items-baseline gap-3">
                {hasSale && (
                  <span className="font-serif text-xl text-muted-foreground line-through">{regularPrice!.toFixed(2).replace('.', ',')} €</span>
                )}
                <span className="font-serif text-3xl font-bold text-accent">{price.toFixed(2).replace('.', ',')} €</span>
                {hasSale && (
                  <span className="bg-accent px-2 py-0.5 text-xs font-medium tracking-wide text-accent-foreground">AKCIJA</span>
                )}
              </div>
            )}
            {(() => {
              const s = cleanHtmlToText(product.short_description || "", 500);
              const l = cleanHtmlToText(product.description || "", 10000);
              let combined = "";
              if (s && l) {
                const sL = s.toLowerCase();
                const lL = l.toLowerCase();
                if (lL.includes(sL)) combined = l;
                else if (sL.includes(lL)) combined = s;
                else combined = `${s}\n\n${l}`;
              } else {
                combined = s || l;
              }
              if (!combined) return null;

              // Tehnične podrobnosti se v uvoženih opisih začnejo z oznako v oglatih oklepajih.
              const specMatch = combined.match(/\[\s*TEHNI[ČC]N[EA][^\]]*\]/i);
              let descText = combined;
              let specText = "";
              if (specMatch && specMatch.index != null) {
                descText = combined.slice(0, specMatch.index).trim();
                specText = combined
                  .slice(specMatch.index + specMatch[0].length)
                  .trim();
              }

              if (!specText) {
                return (
                  <div className="mt-6 text-muted-foreground leading-relaxed whitespace-pre-line">
                    {combined}
                  </div>
                );
              }

              return (
                <div className="mt-6">
                  <div role="tablist" aria-label="Podrobnosti izdelka" className="flex gap-2 border-b border-border">
                    <button
                      type="button"
                      role="tab"
                      id="tab-opis"
                      aria-selected={detailTab === "opis"}
                      aria-controls="panel-opis"
                      onClick={() => setDetailTab("opis")}
                      className={`px-4 py-2 text-sm tracking-wide -mb-px border-b-2 transition-colors ${
                        detailTab === "opis"
                          ? "border-accent text-foreground font-semibold"
                          : "border-transparent text-muted-foreground hover:text-foreground"
                      }`}
                    >
                      Opis izdelka
                    </button>
                    <button
                      type="button"
                      role="tab"
                      id="tab-tehnicne"
                      aria-selected={detailTab === "tehnicne"}
                      aria-controls="panel-tehnicne"
                      onClick={() => setDetailTab("tehnicne")}
                      className={`px-4 py-2 text-sm tracking-wide -mb-px border-b-2 transition-colors ${
                        detailTab === "tehnicne"
                          ? "border-accent text-foreground font-semibold"
                          : "border-transparent text-muted-foreground hover:text-foreground"
                      }`}
                    >
                      Tehnične podrobnosti
                    </button>
                  </div>
                  <div
                    role="tabpanel"
                    id="panel-opis"
                    aria-labelledby="tab-opis"
                    hidden={detailTab !== "opis"}
                    className="pt-4 text-muted-foreground leading-relaxed whitespace-pre-line"
                  >
                    {descText || "Opis izdelka ni na voljo."}
                  </div>
                  <div
                    role="tabpanel"
                    id="panel-tehnicne"
                    aria-labelledby="tab-tehnicne"
                    hidden={detailTab !== "tehnicne"}
                    className="pt-4 text-muted-foreground leading-relaxed whitespace-pre-line"
                  >
                    {specText}
                  </div>
                </div>
              );
            })()}

            {product.stock_quantity != null && (
              <div className="mt-8 text-xs text-muted-foreground">
                {!outOfStock ? `Zaloga: ${product.stock_quantity} kosov` : "Trenutno ni na zalogi"}
              </div>
            )}
            <div className="mt-8 flex flex-wrap items-end gap-4">
              <div className="flex items-center border border-border h-[42px]">
                <button
                  type="button"
                  aria-label="Zmanjšaj"
                  onClick={() => setQty((q) => Math.max(1, q - 1))}
                  className="px-3 h-full hover:bg-muted"
                >
                  <Minus className="h-3.5 w-3.5" />
                </button>
                <input
                  type="number"
                  min={1}
                  max={product.stock_quantity ?? 999}
                  value={qty}
                  aria-label="Količina"
                  onChange={(e) => {
                    const n = Number(e.target.value);
                    if (Number.isFinite(n)) setQty(Math.max(1, Math.min(product.stock_quantity ?? 999, Math.floor(n))));
                  }}
                  className="w-12 bg-transparent text-center text-sm focus:outline-none h-full"
                />
                <button
                  type="button"
                  aria-label="Povečaj"
                  onClick={() => setQty((q) => Math.min(product.stock_quantity ?? 999, q + 1))}
                  className="px-3 h-full hover:bg-muted"
                >
                  <Plus className="h-3.5 w-3.5" />
                </button>
              </div>
              <button
                onClick={() => {
                  if (price == null) return;
                  const item = {
                    id: product.id,
                    slug: product.slug,
                    title: product.title,
                    price,
                    image: product.featured_image_url ?? "",
                    category: "",
                    seller: vendor?.store_name ?? "",
                    sellerSlug: vendor?.slug ?? "",
                    description: "",
                    stock: product.stock_quantity ?? 0,
                  };
                  for (let i = 0; i < qty; i++) add(item);
                }}
                disabled={price == null || outOfStock}
                className="bg-primary text-primary-foreground px-6 py-3 text-sm tracking-wide hover:opacity-90 disabled:opacity-50"
              >
                {outOfStock ? "Razprodano" : "Dodaj v košarico"}
              </button>
            </div>
            <p className="mt-2 text-xs text-muted-foreground">
              Izdelke pošiljamo po plačilu predračuna, brez stroškov poštnine.
            </p>
            <div className="mt-6 flex flex-wrap items-center gap-3">
              <button
                type="button"
                onClick={() => {
                  if (!user) { navigate({ to: "/prijava" }); return; }
                  wishMutation.mutate(product.id);
                }}
                aria-label="Dodaj na seznam želja"
                title="Dodaj na seznam želja"
                className="p-3 border border-border hover:border-accent transition-colors"
              >
                <Heart className={`h-5 w-5 ${wishData?.ids.includes(product.id) ? "fill-accent text-accent" : ""}`} />
              </button>
              <div className="relative">
                <button
                  type="button"
                  onClick={() => setShareOpen((v) => !v)}
                  aria-label="Deli izdelek"
                  title="Deli izdelek"
                  className="p-3 border border-border hover:border-accent transition-colors"
                >
                  <Share2 className="h-5 w-5" />
                </button>
                {shareOpen && (
                  <div className="absolute z-30 mt-2 left-0 bg-background border border-border shadow-lg p-2 min-w-[200px]">
                    <a
                      href={`https://www.facebook.com/sharer/sharer.php?u=${encodeURIComponent(shareUrl)}`}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="flex items-center gap-2 px-3 py-2 text-sm hover:bg-muted"
                      onClick={() => setShareOpen(false)}
                    >
                      <Facebook className="h-4 w-4" /> Facebook
                    </a>
                    <a
                      href={`https://wa.me/?text=${encodeURIComponent(product.title + " — " + shareUrl)}`}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="flex items-center gap-2 px-3 py-2 text-sm hover:bg-muted"
                      onClick={() => setShareOpen(false)}
                    >
                      <svg className="h-4 w-4" viewBox="0 0 24 24" fill="currentColor"><path d="M.057 24l1.687-6.163a11.867 11.867 0 0 1-1.587-5.946C.16 5.335 5.495 0 12.05 0a11.817 11.817 0 0 1 8.413 3.488 11.824 11.824 0 0 1 3.48 8.414c-.003 6.557-5.338 11.892-11.893 11.892a11.9 11.9 0 0 1-5.688-1.448L.057 24zm6.597-3.807c1.676.995 3.276 1.591 5.392 1.592 5.448 0 9.886-4.434 9.889-9.885.002-5.462-4.415-9.89-9.881-9.892-5.452 0-9.887 4.434-9.889 9.884a9.86 9.86 0 0 0 1.512 5.26l-.999 3.648 3.976-1.607zm11.387-5.464c-.074-.124-.272-.198-.57-.347-.297-.149-1.758-.868-2.031-.967-.272-.099-.47-.149-.669.149-.198.297-.768.967-.941 1.165-.173.198-.347.223-.644.074-.297-.149-1.255-.462-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.297-.347.446-.521.151-.172.2-.296.3-.495.099-.198.05-.372-.025-.521-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.501-.669-.51l-.57-.01c-.198 0-.52.074-.792.372s-1.04 1.016-1.04 2.479 1.065 2.876 1.213 3.074c.149.198 2.095 3.2 5.076 4.487.709.306 1.263.489 1.694.626.712.226 1.36.194 1.872.118.571-.085 1.758-.719 2.006-1.413.247-.694.247-1.289.173-1.413z"/></svg>
                      WhatsApp
                    </a>
                    <button
                      type="button"
                      className="w-full flex items-center gap-2 px-3 py-2 text-sm hover:bg-muted text-left"
                      onClick={async () => {
                        try {
                          if (typeof navigator !== "undefined" && navigator.clipboard?.writeText) {
                            await navigator.clipboard.writeText(shareUrl);
                          } else {
                            const ta = document.createElement("textarea");
                            ta.value = shareUrl;
                            ta.setAttribute("readonly", "");
                            ta.style.position = "fixed";
                            ta.style.opacity = "0";
                            document.body.appendChild(ta);
                            ta.select();
                            document.execCommand("copy");
                            document.body.removeChild(ta);
                          }
                          toast.success("Povezava je kopirana");
                        } catch {
                          toast.error("Kopiranje ni uspelo");
                        }
                        setShareOpen(false);
                      }}
                    >
                      <Copy className="h-4 w-4" /> Kopiraj povezavo
                    </button>
                  </div>
                )}
              </div>
              <button
                type="button"
                onClick={() => {
                  if (!user) { navigate({ to: "/prijava" }); return; }
                  setInquiryStatus("idle");
                  setInquiryError(null);
                  if (!inquiryEmail) setInquiryEmail(user.email ?? "");
                  setInquiryOpen(true);
                }}
                className="inline-flex items-center justify-center gap-2 bg-brand text-white px-5 py-3 text-sm hover:opacity-90 transition-opacity w-full sm:w-auto"
              >
                <Mail className="h-4 w-4" />
                Pošljite povpraševanje ponudniku
              </button>
            </div>
          </div>
        </div>

        {/* Sticky mobilni "Dodaj v košarico" */}
        <div className="md:hidden fixed bottom-0 inset-x-0 z-40 bg-background/95 backdrop-blur border-t border-border px-4 py-3 flex items-center gap-3 shadow-lg">
          <div className="flex-1 min-w-0">
            <div className="text-xs text-muted-foreground truncate">{product.title}</div>
            {price != null && (
              <div className="font-serif text-lg font-bold text-accent">
                {hasSale && <span className="mr-2 text-sm font-normal text-muted-foreground line-through">{regularPrice!.toFixed(2).replace('.', ',')} €</span>}
                {price.toFixed(2).replace('.', ',')} €
              </div>
            )}
          </div>
          <button
            onClick={() => {
              if (price == null) return;
              const item = {
                id: product.id,
                slug: product.slug,
                title: product.title,
                price,
                image: product.featured_image_url ?? "",
                category: "",
                seller: vendor?.store_name ?? "",
                sellerSlug: vendor?.slug ?? "",
                description: "",
                stock: product.stock_quantity ?? 0,
              };
              for (let i = 0; i < qty; i++) add(item);
            }}
            disabled={price == null || outOfStock}
            className="bg-primary text-primary-foreground px-5 py-3 text-sm tracking-wide hover:opacity-90 disabled:opacity-50"
          >
            {outOfStock ? "Razprodano" : "V košarico"}
          </button>
        </div>


        {inquiryOpen && (
          <div className="fixed inset-0 z-50 bg-background/80 backdrop-blur-sm flex items-center justify-center p-4" onClick={() => setInquiryOpen(false)}>
            <div className="bg-background border border-border max-w-lg w-full p-8" onClick={(e) => e.stopPropagation()}>
              <div className="eyebrow">Povpraševanje</div>
              <h2 className="font-serif text-2xl mt-2">Pišite ponudniku</h2>
              <p className="mt-1 text-sm text-muted-foreground">
                {vendor?.store_name ? `${vendor.store_name} — ` : ""}{product.title}
              </p>

              {inquiryStatus === "success" ? (
                <>
                  <div className="mt-6 border border-accent/40 bg-accent/5 p-4 text-sm">
                    Vaše povpraševanje je bilo poslano. Prodajalec se vam bo oglasil čim prej.
                  </div>
                  <div className="mt-6 flex justify-end">
                    <button onClick={() => setInquiryOpen(false)} className="border border-border px-4 py-2 text-sm hover:bg-muted">Zapri</button>
                  </div>
                </>
              ) : (
                <form
                  onSubmit={(e) => {
                    e.preventDefault();
                    const emailTrim = inquiryEmail.trim();
                    const emailOk = /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(emailTrim);
                    if (!emailOk) {
                      setInquiryStatus("error");
                      setInquiryError("Vnesite veljaven email naslov.");
                      return;
                    }
                    if (inquiryMessage.trim().length < 5) {
                      setInquiryStatus("error");
                      setInquiryError("Sporočilo je prekratko.");
                      return;
                    }
                    inquiryMutation.mutate({ product_id: product.id, email: emailTrim, message: inquiryMessage.trim() });
                  }}
                  className="mt-6"
                >
                  <label className="block text-xs text-muted-foreground uppercase tracking-wider mb-2">Email <span className="text-destructive">*</span></label>
                  <input
                    type="email"
                    required
                    value={inquiryEmail}
                    onChange={(e) => setInquiryEmail(e.target.value)}
                    placeholder="vase.ime@example.com"
                    className="w-full border border-border bg-background p-3 text-sm focus:outline-none focus:border-foreground mb-4"
                  />
                  <label className="block text-xs text-muted-foreground uppercase tracking-wider mb-2">Sporočilo</label>
                  <textarea
                    value={inquiryMessage}
                    onChange={(e) => setInquiryMessage(e.target.value)}
                    rows={6}
                    maxLength={2000}
                    placeholder="Pozdravljeni, zanima me…"
                    className="w-full border border-border bg-background p-3 text-sm focus:outline-none focus:border-foreground"
                  />
                  {inquiryStatus === "error" && inquiryError && (
                    <div className="mt-2 text-xs text-destructive">{inquiryError}</div>
                  )}
                  <div className="mt-6 flex justify-end gap-3">
                    <button type="button" onClick={() => setInquiryOpen(false)} className="border border-border px-4 py-2 text-sm hover:bg-muted">Prekliči</button>
                    <button type="submit" disabled={inquiryMutation.isPending} className="bg-primary text-primary-foreground px-5 py-2 text-sm hover:opacity-90 disabled:opacity-50">
                      {inquiryMutation.isPending ? "Pošiljam…" : "Pošlji povpraševanje"}
                    </button>
                  </div>
                </form>
              )}
            </div>
          </div>
        )}

        {vendor && (
          <div className="mt-16 bg-background border border-border p-6 md:p-10 shadow-sm">
            <div className="flex items-start justify-between gap-4">
              <div className="min-w-0">
                <div className="eyebrow">Prodajalec</div>
                <Link
                  to="/prodajalci/$slug"
                  params={{ slug: vendor.slug! }}
                  className="font-serif text-3xl md:text-4xl mt-2 text-brand hover:text-accent transition-colors inline-block"
                  title="Poglej več izdelkov ponudnika"
                >
                  {vendor.store_name}
                </Link>
              </div>
              {(() => {
                const instagram = ensureUrl(vendor.instagram ?? null, "instagram.com");
                const youtube = ensureUrl(vendor.youtube ?? null, "youtube.com");
                const linkedin = ensureUrl(vendor.linkedin ?? null, "linkedin.com/in");
                const facebook = ensureUrl(vendor.facebook ?? null, "facebook.com");
                const tiktok = ensureUrl(vendor.tiktok ?? null, "tiktok.com/@");
                const hasSocial = instagram || youtube || linkedin || facebook || tiktok;
                if (!hasSocial) return null;
                return (
                <div className="flex items-center gap-2 shrink-0 pt-1">
                    {instagram && (
                      <a href={instagram} target="_blank" rel="noopener noreferrer" aria-label="Instagram" className="w-9 h-9 flex items-center justify-center border border-accent text-accent hover:bg-accent hover:text-accent-foreground transition-colors">
                        <Instagram className="h-4 w-4" />
                      </a>
                    )}
                    {facebook && (
                      <a href={facebook} target="_blank" rel="noopener noreferrer" aria-label="Facebook" className="w-9 h-9 flex items-center justify-center border border-accent text-accent hover:bg-accent hover:text-accent-foreground transition-colors">
                        <Facebook className="h-4 w-4" />
                      </a>
                    )}
                    {tiktok && (
                      <a href={tiktok} target="_blank" rel="noopener noreferrer" aria-label="TikTok" className="w-9 h-9 flex items-center justify-center border border-accent text-accent hover:bg-accent hover:text-accent-foreground transition-colors">
                        <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="h-4 w-4">
                          <path d="M9 12a4 4 0 1 0 4 4V4c0 1.5.8 3 2.5 3.5"/>
                          <path d="M19 8v8"/>
                        </svg>
                      </a>
                    )}
                    {youtube && (
                      <a href={youtube} target="_blank" rel="noopener noreferrer" aria-label="YouTube" className="w-9 h-9 flex items-center justify-center border border-accent text-accent hover:bg-accent hover:text-accent-foreground transition-colors">
                        <Youtube className="h-4 w-4" />
                      </a>
                    )}
                    {linkedin && (
                      <a href={linkedin} target="_blank" rel="noopener noreferrer" aria-label="LinkedIn" className="w-9 h-9 flex items-center justify-center border border-accent text-accent hover:bg-accent hover:text-accent-foreground transition-colors">
                        <Linkedin className="h-4 w-4" />
                      </a>
                    )}
                  </div>
                );
              })()}
            </div>

            <div className="mt-3 flex flex-wrap items-center gap-x-4 gap-y-2 text-sm text-muted-foreground">
              {vendor.city && (
                <span className="inline-flex items-center gap-1.5">
                  <MapPin className="h-4 w-4" />
                  {vendor.city}
                </span>
              )}
              <span className="inline-flex items-center gap-1.5 text-accent">● aktiven</span>
            </div>

            {(vendor.email || vendor.phone) && (
              <div className="mt-5 flex flex-wrap items-center gap-x-6 gap-y-2 text-sm">
                {vendor.email && (
                  <a
                    href={`mailto:${vendor.email}`}
                    onClick={() => { trackVisit({ data: { vendorId: vendor.id, kind: "email", path: `/izdelki/${product.slug ?? product.id}` } }).catch(() => {}); }}
                    className="inline-flex items-center gap-2 hover:text-accent transition-colors"
                  >
                    <Mail className="h-4 w-4" />
                    {vendor.email}
                  </a>
                )}
                {vendor.phone && (
                  <a
                    href={`tel:${vendor.phone.replace(/\s+/g, "")}`}
                    onClick={() => { trackVisit({ data: { vendorId: vendor.id, kind: "phone", path: `/izdelki/${product.slug ?? product.id}` } }).catch(() => {}); }}
                    className="inline-flex items-center gap-2 hover:text-accent transition-colors"
                  >
                    <Phone className="h-4 w-4" />
                    {formatPhone(vendor.phone)}
                  </a>
                )}
              </div>
            )}

            {vendor.description && (
              <div className="mt-8 max-w-3xl text-muted-foreground leading-relaxed prose prose-sm" dangerouslySetInnerHTML={{ __html: sanitizeHtml(vendor.description) }} />
            )}
          </div>
        )}

        {(data?.similar ?? []).length > 0 && (
          <div className="mt-24">
            <h2 className="font-serif text-3xl mb-8">Več izdelkov tega ponudnika</h2>
            <div className="grid grid-cols-2 md:grid-cols-4 gap-x-6 gap-y-12">
              {(data?.similar ?? []).map((p) => <ProductCard key={p.id} product={p} />)}
            </div>
          </div>
        )}

        <Reviews productId={product.id} />

      </section>
    </>
  );
}
